import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  AlertTriangle, CheckCircle, RefreshCw, Loader2, Shield,
  BarChart3, FileText, Target, Layout, AlertCircle,
  ChevronDown, ChevronUp, Save, Download, HelpCircle,
  TrendingUp, Check, X, Flag,
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';
import { toast } from 'sonner';

// ─── Types ───

interface EngagementInsightsStepProps {
  companyId: string;
  accessToken: string;
  currentUser?: { id: string; name?: string; email?: string };
}

interface StructuredInsight {
  observation: string;
  impact: string;
  action: string;
  details?: string[];
  detailType?: 'pain_points' | 'metrics' | 'objectives' | 'systems';
  severity?: 'risk' | 'info' | 'clarification';
}

type MetricType = 'native' | 'derived' | 'custom';

interface KpiItem {
  name: string;
  source: string;
  rationale: string;
  metricType?: MetricType;
}

interface ChangeOrder {
  detected_changes: Array<{ item: string; type: string; details: string }>;
  impact_summary: Array<{ workstream: string; impact: string }>;
  risks_if_unaddressed: string[];
  suggested_language: string;
  _note?: string;
}

interface ReportingBlueprint {
  kpis: KpiItem[];
  dataFields: Array<{ field: string; label?: string; reason: string }>;
  dashboards: Array<{ name: string; description: string }>;
  missingInputs?: StructuredInsight[];
  dataGaps?: string[];
}

interface Readiness {
  score: number;
  level: 'high' | 'medium' | 'low';
  risks: StructuredInsight[] | string[];
  actions: StructuredInsight[] | string[];
  clarifications?: StructuredInsight[];
  confidence: { overall: string; warnings: string[] };
}

interface Insights {
  change_order: ChangeOrder;
  reporting_blueprint: ReportingBlueprint;
  readiness: Readiness;
  generatedAt: string;
}

type InsightStatus = 'open' | 'acknowledged' | 'flagged' | 'dismissed';

// ─── Helpers ───

const levelColors: Record<string, { bg: string; text: string; border: string }> = {
  high: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  low: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

const levelLabel: Record<string, string> = {
  high: 'Ready to Proceed',
  medium: 'Action Needed',
  low: 'Significant Gaps',
};

const changeTypeLabels: Record<string, string> = {
  new_objective: 'New Objective',
  new_system: 'New System / Integration',
  new_requirement: 'New Requirement',
  timeline_change: 'Timeline Change',
  compliance_addition: 'Compliance Addition',
  scope_expansion: 'Scope Expansion',
};

const detailTypeLabels: Record<string, string> = {
  pain_points: 'pain points',
  metrics: 'metrics',
  objectives: 'objectives',
  systems: 'systems',
};

const metricTypeBadge: Record<MetricType, { label: string; className: string; tooltip: string }> = {
  native: { label: 'Native', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', tooltip: 'Available out of the box in Jira Service Management' },
  derived: { label: 'Derived', className: 'bg-blue-50 text-blue-700 border-blue-200', tooltip: 'Calculated from native JSM fields (e.g., averages, rates)' },
  custom: { label: 'Custom', className: 'bg-purple-50 text-purple-700 border-purple-200', tooltip: 'Requires custom fields, automation, or external data' },
};

function humanizeFieldName(raw: string): string {
  const jsmMap: Record<string, string> = {
    priority: 'Priority', service_category: 'Service Category',
    resolution_time: 'Resolution Time', assignee: 'Assignee',
    linked_change_request: 'Linked Change Request',
    satisfaction_rating: 'Satisfaction Rating',
    request_channel: 'Request Channel', root_cause: 'Root Cause',
  };
  const lower = raw.toLowerCase().trim();
  if (jsmMap[lower]) return jsmMap[lower];
  return raw.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

function toStructuredInsight(item: StructuredInsight | string): StructuredInsight {
  if (typeof item === 'string') return { observation: item, impact: '', action: '' };
  return item;
}

function getInsightStatuses(companyId: string): Record<string, InsightStatus> {
  try {
    const raw = localStorage.getItem(`pk_insight_statuses_${companyId}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function saveInsightStatuses(companyId: string, statuses: Record<string, InsightStatus>) {
  try { localStorage.setItem(`pk_insight_statuses_${companyId}`, JSON.stringify(statuses)); } catch {}
}

function getMetricTypeOverrides(companyId: string): Record<string, MetricType> {
  try {
    const raw = localStorage.getItem(`pk_metric_type_overrides_${companyId}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function saveMetricTypeOverrides(companyId: string, overrides: Record<string, MetricType>) {
  try { localStorage.setItem(`pk_metric_type_overrides_${companyId}`, JSON.stringify(overrides)); } catch {}
}

function insightKey(observation: string): string {
  return observation.slice(0, 80).replace(/\s+/g, '_').toLowerCase();
}

// ─── Status indicator for card top-right ───
function StatusIndicator({ status }: { status: InsightStatus }) {
  if (status === 'open') return null;
  const map: Record<InsightStatus, { icon: React.ReactNode; className: string }> = {
    open: { icon: null, className: '' },
    acknowledged: { icon: <Check className="w-2.5 h-2.5" />, className: 'bg-emerald-500 text-white' },
    flagged: { icon: <Flag className="w-2.5 h-2.5" />, className: 'bg-amber-500 text-white' },
    dismissed: { icon: <X className="w-2.5 h-2.5" />, className: 'bg-gray-400 text-white' },
  };
  const s = map[status];
  return (
    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0 ${s.className}`}>
      {s.icon}
    </span>
  );
}

// ─── InsightCard ───

function InsightCard({
  insight,
  accentColor = 'amber',
  status,
  onStatusChange,
  itemStatuses,
  onItemStatusChange,
}: {
  insight: StructuredInsight;
  accentColor?: 'amber' | 'red' | 'blue' | 'gray' | 'purple';
  status: InsightStatus;
  onStatusChange: (s: InsightStatus) => void;
  /** Per-detail-item statuses for item-level actions */
  itemStatuses?: Record<string, InsightStatus>;
  onItemStatusChange?: (itemKey: string, s: InsightStatus) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = insight.details && insight.details.length > 0;
  const hasStructure = !!(insight.impact || insight.action);
  const detailLabel = insight.detailType ? detailTypeLabels[insight.detailType] || 'items' : 'items';

  const borderMap: Record<string, string> = {
    amber: 'border-l-amber-400',
    red: 'border-l-red-400',
    blue: 'border-l-blue-400',
    gray: 'border-l-gray-300',
    purple: 'border-l-purple-400',
  };

  const isDimmed = status === 'acknowledged' || status === 'dismissed';

  // Show first 2 items inline as preview
  const previewItems = hasDetails ? insight.details!.slice(0, 2) : [];
  const remainingCount = hasDetails ? Math.max(0, insight.details!.length - 2) : 0;

  // Mini action buttons for individual detail items
  const ItemActions = ({ itemKey }: { itemKey: string }) => {
    if (!onItemStatusChange || !itemStatuses) return null;
    const st = itemStatuses[itemKey] || 'open';
    return (
      <div className="flex items-center gap-1 ml-auto shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); onItemStatusChange(itemKey, st === 'acknowledged' ? 'open' : 'acknowledged'); }}
          className={`p-0.5 rounded-full transition-all ${
            st === 'acknowledged' ? 'bg-emerald-100 text-emerald-600' : 'text-gray-300 hover:text-emerald-500 hover:bg-emerald-50'
          }`}
          title={st === 'acknowledged' ? 'Confirmed' : 'Confirm'}
        >
          <Check className="w-2.5 h-2.5" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onItemStatusChange(itemKey, st === 'flagged' ? 'open' : 'flagged'); }}
          className={`p-0.5 rounded-full transition-all ${
            st === 'flagged' ? 'bg-amber-100 text-amber-600' : 'text-gray-300 hover:text-amber-500 hover:bg-amber-50'
          }`}
          title={st === 'flagged' ? 'Flagged' : 'Flag for follow-up'}
        >
          <Flag className="w-2.5 h-2.5" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onItemStatusChange(itemKey, st === 'dismissed' ? 'open' : 'dismissed'); }}
          className={`p-0.5 rounded-full transition-all ${
            st === 'dismissed' ? 'bg-gray-200 text-gray-500' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'
          }`}
          title={st === 'dismissed' ? 'Dismissed' : 'Not applicable'}
        >
          <X className="w-2.5 h-2.5" />
        </button>
      </div>
    );
  };

  return (
    <div className={`border border-gray-200 rounded-md overflow-hidden transition-opacity ${isDimmed ? 'opacity-50' : ''}`}>
      <div className={`border-l-[3px] ${borderMap[accentColor] || borderMap.amber}`}>
        {/* Observation headline + status indicator */}
        <div className="px-3 pt-2.5 pb-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[13px] font-semibold text-gray-900 leading-snug" style={{ fontWeight: 600 }}>
              {insight.observation}
            </p>
            <StatusIndicator status={status} />
          </div>

          {/* Inline preview of first 1-2 detail items */}
          {hasDetails && (
            <div className="mt-1.5">
              {previewItems.map((d, i) => {
                const itemKey = insightKey(d);
                return (
                  <div key={i} className="flex items-center gap-1.5 leading-relaxed">
                    <p className="text-[11px] text-gray-500 flex items-start gap-1.5 flex-1 min-w-0">
                      <span className="text-gray-300 mt-px shrink-0">&bull;</span>
                      {d}
                    </p>
                    {expanded && <ItemActions itemKey={itemKey} />}
                  </div>
                );
              })}
              {(remainingCount > 0 || expanded) && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="text-[11px] text-blue-600 hover:text-blue-800 mt-0.5 flex items-center gap-0.5"
                >
                  {expanded ? 'Show less' : `View all ${insight.details!.length} ${detailLabel}`}
                  <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </button>
              )}
              {expanded && (
                <div className="mt-1 space-y-0.5">
                  {insight.details!.slice(2).map((d, i) => {
                    const itemKey = insightKey(d);
                    return (
                      <div key={i + 2} className="flex items-center gap-1.5 leading-relaxed">
                        <p className="text-[11px] text-gray-500 flex items-start gap-1.5 flex-1 min-w-0">
                          <span className="text-gray-300 mt-px shrink-0">&bull;</span>
                          {d}
                        </p>
                        <ItemActions itemKey={itemKey} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Why it matters / Next step with clear visual separation */}
        {hasStructure && (
          <div className="px-3 pb-2 mt-1 space-y-0">
            {insight.impact && (
              <div className="flex items-start gap-2 py-1.5 border-t border-gray-100">
                <span className="text-gray-400 font-semibold uppercase tracking-wider shrink-0 w-24 pt-px" style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.05em' }}>
                  Why it matters
                </span>
                <p className="text-[11px] text-gray-500 leading-relaxed">{insight.impact}</p>
              </div>
            )}
            {insight.action && (
              <div className="flex items-start gap-2 py-1.5 border-t border-gray-100">
                <span className="text-purple-500 font-semibold uppercase tracking-wider shrink-0 w-24 pt-px" style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.05em' }}>
                  Next step
                </span>
                <p className="text-[11px] text-gray-800 leading-relaxed font-semibold" style={{ fontWeight: 600 }}>{insight.action}</p>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 px-3 pb-2 pt-0.5">
          <button
            onClick={() => onStatusChange(status === 'acknowledged' ? 'open' : 'acknowledged')}
            className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full transition-all ${
              status === 'acknowledged'
                ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300 font-medium'
                : 'text-gray-400 hover:bg-emerald-50 hover:text-emerald-600'
            }`}
            style={{ fontWeight: status === 'acknowledged' ? 500 : 400 }}
          >
            <Check className="w-2.5 h-2.5" />
            {status === 'acknowledged' ? 'Confirmed' : 'Confirm'}
          </button>
          <button
            onClick={() => onStatusChange(status === 'flagged' ? 'open' : 'flagged')}
            className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full transition-all ${
              status === 'flagged'
                ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300 font-medium'
                : 'text-gray-400 hover:bg-amber-50 hover:text-amber-600'
            }`}
            style={{ fontWeight: status === 'flagged' ? 500 : 400 }}
          >
            <Flag className="w-2.5 h-2.5" />
            {status === 'flagged' ? 'Flagged' : 'Flag'}
          </button>
          <button
            onClick={() => onStatusChange(status === 'dismissed' ? 'open' : 'dismissed')}
            className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full transition-all ${
              status === 'dismissed'
                ? 'bg-gray-200 text-gray-600 ring-1 ring-gray-400 font-medium'
                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            }`}
            style={{ fontWeight: status === 'dismissed' ? 500 : 400 }}
          >
            <X className="w-2.5 h-2.5" />
            {status === 'dismissed' ? 'Dismissed' : 'N/A'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Metric Type Selector ───

function MetricTypeBadgeReadOnly({ value }: { value: MetricType }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const badge = metricTypeBadge[value];
  return (
    <div className="relative inline-flex items-center">
      <div
        className="relative"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span
          className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium ${badge.className}`}
          style={{ fontWeight: 500 }}
        >
          {badge.label}
        </span>
        {showTooltip && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-30 px-2.5 py-1.5 bg-gray-900 text-white text-[10px] rounded-md shadow-lg whitespace-nowrap pointer-events-none max-w-[220px]" style={{ whiteSpace: 'normal' }}>
            {badge.tooltip}
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[4px] border-r-[4px] border-t-[4px] border-l-transparent border-r-transparent border-t-gray-900" />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───

export function EngagementInsightsStep({ companyId, accessToken, currentUser }: EngagementInsightsStepProps) {
  const [insights, setInsights] = useState<Insights | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [savingArtifact, setSavingArtifact] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    clarify_summary: true,
    change_order: true,
    reporting: true,
    readiness: true,
  });
  const [insightStatuses, setInsightStatuses] = useState<Record<string, InsightStatus>>(() => getInsightStatuses(companyId));
  const [metricOverrides, setMetricOverrides] = useState<Record<string, MetricType>>(() => getMetricTypeOverrides(companyId));

  const updateStatus = useCallback((key: string, status: InsightStatus) => {
    setInsightStatuses(prev => {
      const next = { ...prev, [key]: status };
      saveInsightStatuses(companyId, next);
      return next;
    });
  }, [companyId]);

  const updateMetricType = useCallback((kpiName: string, type: MetricType) => {
    setMetricOverrides(prev => {
      const next = { ...prev, [kpiName]: type };
      saveMetricTypeOverrides(companyId, next);
      return next;
    });
  }, [companyId]);

  const getEffectiveMetricType = useCallback((kpi: KpiItem): { type: MetricType; overridden: boolean } => {
    const override = metricOverrides[kpi.name];
    if (override) return { type: override, overridden: true };
    return { type: kpi.metricType || 'custom', overridden: false };
  }, [metricOverrides]);

  const fetchInsights = useCallback(async (regenerate?: string) => {
    if (regenerate) setRegenerating(regenerate); else setLoading(true);
    try {
      const url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-insights/${companyId}${regenerate ? `?regenerate=${regenerate}` : ''}`;
      const resp = await fetch(url, { headers: getAuthHeaders(accessToken) });
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).error || 'Failed to load insights');
      const data = await resp.json();
      if (data.success && data.insights) {
        setInsights(data.insights);
        if (regenerate) toast.success('Insights regenerated');
      }
    } catch (err) {
      console.error('[INSIGHTS] Fetch failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load insights');
    } finally { setLoading(false); setRegenerating(null); }
  }, [companyId, accessToken]);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  const toggleSection = (key: string) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const saveArtifact = async (artifactType: string, data: any) => {
    setSavingArtifact(artifactType);
    try {
      const resp = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-insights/${companyId}/save-artifact`,
        { method: 'POST', headers: getAuthHeadersWithJson(accessToken), body: JSON.stringify({ artifactType, data, userId: currentUser?.id, userName: currentUser?.name || currentUser?.email }) }
      );
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).error || 'Failed to save artifact');
      toast.success('Saved as artifact');
    } catch (err) {
      console.error('[INSIGHTS] Save artifact failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save artifact');
    } finally { setSavingArtifact(null); }
  };

  const generatePdf = async (artifactType: string, data: any) => {
    setGeneratingPdf(artifactType);
    try {
      // Apply metric type overrides to KPI data before export
      let exportData = data;
      if (artifactType === 'reporting_blueprint' || artifactType === 'full_insights') {
        const applyOverrides = (bp: any) => {
          if (!bp?.kpis) return bp;
          return {
            ...bp,
            kpis: bp.kpis.map((k: KpiItem) => ({
              ...k,
              metricType: metricOverrides[k.name] || k.metricType || 'custom',
            })),
          };
        };
        if (artifactType === 'reporting_blueprint') {
          exportData = applyOverrides(data);
        } else {
          exportData = { ...data, reporting_blueprint: applyOverrides(data.reporting_blueprint) };
        }
      }

      const resp = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/documents/${companyId}/insight-document`,
        { method: 'POST', headers: getAuthHeadersWithJson(accessToken), body: JSON.stringify({ artifactType, data: exportData, userId: currentUser?.id, userName: currentUser?.name || currentUser?.email }) }
      );
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).error || 'Failed to generate document');
      const result = await resp.json();
      if (result.pdfBase64) {
        const byteChars = atob(result.pdfBase64);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename || `${artifactType}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Document generated and downloaded');
      }
    } catch (err) {
      console.error('[INSIGHTS] Document generation failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to generate document');
    } finally { setGeneratingPdf(null); }
  };

  const ActionBar = ({ artifactType, data, regenKey }: { artifactType: string; data: any; regenKey: string }) => (
    <div className="flex items-center gap-2 px-4 pb-3 pt-2 border-t border-gray-100 mt-1">
      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); fetchInsights(regenKey); }} disabled={!!regenerating} className="text-xs">
        {regenerating === regenKey ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
        {regenKey === 'readiness' ? 'Recalculate' : 'Regenerate'}
      </Button>
      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); saveArtifact(artifactType, data); }} disabled={!!savingArtifact} className="text-xs">
        {savingArtifact === artifactType ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
        Save as Artifact
      </Button>
      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); generatePdf(artifactType, data); }} disabled={!!generatingPdf} className="text-xs">
        {generatingPdf === artifactType ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
        Export PDF
      </Button>
    </div>
  );

  const SectionHeader = ({ icon, iconColor, title, badge, sectionKey, onRegenerate }: {
    icon: React.ReactNode; iconColor: string; title: string; badge?: React.ReactNode; sectionKey: string; onRegenerate?: () => void;
  }) => (
    <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50/70 transition-colors" onClick={() => toggleSection(sectionKey)}>
      <div className="flex items-center gap-2">
        <span className={iconColor}>{icon}</span>
        <span className="text-sm font-semibold text-gray-900" style={{ fontSize: '0.875rem', fontWeight: 600 }}>{title}</span>
        {badge}
      </div>
      <div className="flex items-center gap-1">
        {onRegenerate && (
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onRegenerate(); }} disabled={!!regenerating} className="text-xs h-7 w-7 p-0">
            {regenerating === sectionKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        )}
        {expandedSections[sectionKey] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </div>
    </div>
  );

  const SubHeading = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
    <div className="flex items-center gap-1.5 mb-2">
      {icon}
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide" style={{ fontSize: '0.6875rem', fontWeight: 600 }}>{children}</h4>
    </div>
  );

  // ─── Loading / Empty ───
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        <p className="text-sm text-gray-500 font-medium" style={{ fontWeight: 500 }}>Analyzing engagement data...</p>
        <p className="text-xs text-gray-400">Generating evidence-based delivery insights from your project goals</p>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No insights available yet. Complete the previous steps first.</p>
      </div>
    );
  }

  const { change_order: rawChangeOrder, reporting_blueprint: rawReportingBlueprint, readiness: rawReadiness } = insights;

  // ─── Normalize: guarantee every section is a safe shape (handles null / partial / legacy cached data) ───
  const readiness: Readiness = {
    score: rawReadiness?.score ?? 0,
    level: rawReadiness?.level ?? 'medium',
    risks: rawReadiness?.risks ?? [],
    actions: rawReadiness?.actions ?? [],
    clarifications: rawReadiness?.clarifications ?? [],
    confidence: rawReadiness?.confidence ?? { overall: '', warnings: [] },
  };

  const reporting_blueprint: ReportingBlueprint = {
    kpis: rawReportingBlueprint?.kpis ?? [],
    dataFields: rawReportingBlueprint?.dataFields ?? [],
    dashboards: rawReportingBlueprint?.dashboards ?? [],
    missingInputs: rawReportingBlueprint?.missingInputs ?? undefined,
    dataGaps: rawReportingBlueprint?.dataGaps ?? [],
  };

  const change_order: ChangeOrder = {
    detected_changes: rawChangeOrder?.detected_changes ?? [],
    impact_summary: rawChangeOrder?.impact_summary ?? [],
    risks_if_unaddressed: rawChangeOrder?.risks_if_unaddressed ?? [],
    suggested_language: rawChangeOrder?.suggested_language ?? '',
    _note: rawChangeOrder?._note,
  };

  const readinessColor = levelColors[readiness.level] || levelColors.medium;

  // Normalize all insights
  const structuredRisks = (readiness.risks || []).map(toStructuredInsight);
  const structuredClarifications = (readiness.clarifications || []).map(toStructuredInsight);
  // Fix #5: Client-facing language for fallback insights (no consultant-oriented language)
  const effectiveClarifications = structuredClarifications.length > 0
    ? structuredClarifications
    : (readiness.confidence?.warnings || []).map((w: string) => ({
        observation: w,
        impact: 'This may affect the accuracy of delivery planning and reporting',
        action: 'Review this item and confirm whether it has been addressed',
      }));
  const reportingMissingInputs: StructuredInsight[] = reporting_blueprint.missingInputs
    ? reporting_blueprint.missingInputs
    : (reporting_blueprint.dataGaps || []).map((g: string) => ({
        observation: g, impact: 'May affect reporting accuracy or completeness', action: 'Confirm whether this information is available or needs to be gathered',
      }));

  const targetPattern = /\b\d+\s*%|\btarget\b|\breduction\b|\bincrease\b|\bdecrease\b|\bimprove\b|\bfaster\b|\bsatisfaction\b.*\d/i;
  const kpiMetrics = reporting_blueprint.kpis.filter(k => !targetPattern.test(k.name) && !targetPattern.test(k.rationale));
  const kpiTargets = reporting_blueprint.kpis.filter(k => targetPattern.test(k.name) || targetPattern.test(k.rationale));

  // ─── Aggregate "What to Clarify" items (deduplicated) ───
  const allClarifyItems: Array<StructuredInsight & { source: string }> = [];
  const seenKeys = new Set<string>();
  for (const c of effectiveClarifications) {
    const key = insightKey(c.observation);
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      allClarifyItems.push({ ...c, source: 'Delivery Readiness' });
    }
  }
  for (const m of reportingMissingInputs) {
    const key = insightKey(m.observation);
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      allClarifyItems.push({ ...m, source: 'Reporting Blueprint' });
    }
  }
  // Fix #6: Build a set of keys already shown in "What to Clarify" to suppress duplicates in subsections
  const clarifyKeySet = new Set(allClarifyItems.map(item => insightKey(item.observation)));

  // Filter reporting missing inputs to exclude items already in "What to Clarify"
  const nonDuplicateReportingInputs = reportingMissingInputs.filter(
    item => !clarifyKeySet.has(insightKey(item.observation))
  );

  const openClarifyItems = allClarifyItems.filter(item => {
    const key = insightKey(item.observation);
    const st = insightStatuses[key];
    return st !== 'acknowledged' && st !== 'dismissed';
  });

  const totalRisks = structuredRisks.length;
  const totalOpen = openClarifyItems.length;

  return (
    <div className="space-y-4">
      {/* ━━━ Page Header ━━━ */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900" style={{ fontSize: '1.125rem', fontWeight: 600 }}>
            Engagement Insights
          </h2>
          <p className="text-[11px] text-gray-400 mt-1">
            Generated {insights.generatedAt ? new Date(insights.generatedAt).toLocaleString() : 'recently'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => generatePdf('full_insights', { readiness, reporting_blueprint, change_order })}
            disabled={!!generatingPdf}
            className="text-xs"
          >
            {generatingPdf === 'full_insights' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            Export All
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchInsights('all')} disabled={!!regenerating} className="text-xs">
            {regenerating === 'all' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Regenerate All
          </Button>
        </div>
      </div>

      {/* ━━━ Readiness Score Banner ━━━ */}
      <div className={`rounded-lg border ${readinessColor.border} ${readinessColor.bg} p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full border-2 ${readinessColor.border} flex items-center justify-center bg-white/60`}>
              <span className={`text-lg font-bold ${readinessColor.text}`} style={{ fontSize: '1.125rem', fontWeight: 700 }}>{readiness.score}</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900" style={{ fontSize: '0.875rem', fontWeight: 600 }}>Delivery Readiness</span>
                <Badge variant="outline" className={`${readinessColor.text} ${readinessColor.border} text-xs`}>
                  {levelLabel[readiness.level] || readiness.level}
                </Badge>
              </div>
              <p className="text-xs text-gray-600 mt-0.5">
                {totalRisks > 0 ? `${totalRisks} delivery risk${totalRisks !== 1 ? 's' : ''}` : 'No delivery risks'}
                {totalOpen > 0 && <> &middot; {totalOpen} item{totalOpen !== 1 ? 's' : ''} to clarify</>}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => fetchInsights('readiness')} disabled={!!regenerating} className="h-7 w-7 p-0">
            {regenerating === 'readiness' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* ━━━ WHAT TO CLARIFY ━━━ */}
      {allClarifyItems.length > 0 && (
        <Card className="border border-gray-200 overflow-hidden">
          <SectionHeader
            icon={<HelpCircle className="w-4 h-4" />}
            iconColor="text-blue-600"
            title="What to Clarify"
            badge={openClarifyItems.length > 0 ? (
              <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                {openClarifyItems.length} open
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200">
                All addressed
              </Badge>
            )}
            sectionKey="clarify_summary"
          />
          {expandedSections.clarify_summary && (
            <CardContent className="pt-0 pb-3 px-4">
              <p className="text-xs text-gray-500 mb-3">
                These items need clarification or confirmation before delivery planning can proceed with full confidence.
                Mark each as confirmed, flagged for follow-up, or not applicable.
              </p>
              <div className="space-y-2">
                {allClarifyItems.map((item, i) => {
                  const key = insightKey(item.observation);
                  return (
                    <div key={`cl-${i}`} className="relative">
                      <div className="mb-0.5">
                        <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wider" style={{ fontWeight: 500 }}>{item.source}</span>
                      </div>
                      <InsightCard
                        insight={item}
                        accentColor={item.severity === 'risk' ? 'amber' : item.severity === 'info' ? 'blue' : 'gray'}
                        status={insightStatuses[key] || 'open'}
                        onStatusChange={(s) => updateStatus(key, s)}
                        itemStatuses={insightStatuses}
                        onItemStatusChange={(itemKey, s) => updateStatus(itemKey, s)}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* ━━━ Delivery Readiness ━━━ */}
      <Card className="border border-gray-200 overflow-hidden">
        <SectionHeader icon={<Shield className="w-4 h-4" />} iconColor={readinessColor.text} title="Delivery Readiness" sectionKey="readiness" />
        {expandedSections.readiness && (
          <CardContent className="pt-0 pb-4 px-4 space-y-5">
            {structuredRisks.length > 0 && (
              <div>
                <SubHeading icon={<AlertTriangle className="w-3 h-3 text-amber-500" />}>Delivery Risks</SubHeading>
                <div className="space-y-2">
                  {structuredRisks.map((risk, i) => {
                    const key = insightKey(risk.observation);
                    return (
                      <InsightCard
                        key={`dr-${i}`}
                        insight={risk}
                        accentColor={risk.severity === 'info' ? 'blue' : 'amber'}
                        status={insightStatuses[key] || 'open'}
                        onStatusChange={(s) => updateStatus(key, s)}
                        itemStatuses={insightStatuses}
                        onItemStatusChange={(itemKey, s) => updateStatus(itemKey, s)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {structuredRisks.length === 0 && effectiveClarifications.length === 0 && (
              <div className="flex items-center gap-2 text-xs text-emerald-600 px-1">
                <CheckCircle className="w-3.5 h-3.5" />
                <span>No delivery risks or open items identified.</span>
              </div>
            )}
          </CardContent>
        )}
        <ActionBar artifactType="engagement_readiness" data={readiness} regenKey="readiness" />
      </Card>

      {/* ━━━ Scope Change Detection ━━━ */}
      <Card className="border border-gray-200 overflow-hidden">
        <SectionHeader
          icon={<FileText className="w-4 h-4" />} iconColor="text-blue-600"
          title="Scope Change Detection"
          badge={change_order.detected_changes.length > 0 ? (
            <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
              {change_order.detected_changes.length} change{change_order.detected_changes.length !== 1 ? 's' : ''}
            </Badge>
          ) : undefined}
          sectionKey="change_order" onRegenerate={() => fetchInsights('change_order')}
        />
        {expandedSections.change_order && (
          <CardContent className="pt-0 pb-4 px-4 space-y-4">
            {change_order._note === 'no_sow' ? (
              <p className="text-xs text-gray-500 italic">No SOW uploaded. Upload a SOW to enable scope change detection.</p>
            ) : change_order.detected_changes.length === 0 ? (
              <div className="flex items-center gap-2 text-xs text-emerald-600">
                <CheckCircle className="w-3.5 h-3.5" />
                <span>No scope changes detected between SOW and current engagement context.</span>
              </div>
            ) : (
              <>
                <div>
                  <SubHeading icon={<Target className="w-3 h-3 text-blue-500" />}>Detected Scope Changes</SubHeading>
                  <div className="space-y-2">
                    {change_order.detected_changes.map((c, i) => (
                      <div key={`ch-${i}`} className="bg-blue-50 border border-blue-100 rounded-md p-3">
                        <div className="flex items-center gap-2 mb-1.5">
                          <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200">
                            {changeTypeLabels[c.type] || c.type}
                          </Badge>
                          <span className="text-xs font-medium text-gray-900" style={{ fontWeight: 500 }}>{c.item}</span>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{c.details}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {change_order.impact_summary.length > 0 && (
                  <div>
                    <SubHeading icon={<TrendingUp className="w-3 h-3 text-blue-500" />}>Impact on Workstreams</SubHeading>
                    <div className="space-y-1.5">
                      {change_order.impact_summary.map((s, i) => (
                        <div key={`im-${i}`} className="flex items-start gap-2 text-xs">
                          <Target className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                          <span><strong style={{ fontWeight: 600 }}>{s.workstream}:</strong> {s.impact}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {change_order.risks_if_unaddressed.length > 0 && (
                  <div>
                    <SubHeading icon={<AlertTriangle className="w-3 h-3 text-red-500" />}>Risk if Not Addressed</SubHeading>
                    <div className="space-y-1">
                      {change_order.risks_if_unaddressed.map((r, i) => (
                        <div key={`ri-${i}`} className="flex items-start gap-2 text-xs text-red-700">
                          <AlertTriangle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
                          <span>{r}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {change_order.suggested_language && (
                  <div>
                    <SubHeading icon={<FileText className="w-3 h-3 text-gray-500" />}>Suggested Change Order Language</SubHeading>
                    <div className="bg-gray-50 rounded-md p-3 text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {change_order.suggested_language}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        )}
        <ActionBar artifactType="change_order_insight" data={change_order} regenKey="change_order" />
      </Card>

      {/* ━━━ Reporting Blueprint ━━━ */}
      <Card className="border border-gray-200 overflow-hidden">
        <SectionHeader
          icon={<BarChart3 className="w-4 h-4" />} iconColor="text-indigo-600"
          title="Reporting Blueprint"
          badge={<Badge variant="outline" className="text-xs text-indigo-600 border-indigo-200">{reporting_blueprint.kpis.length} KPI{reporting_blueprint.kpis.length !== 1 ? 's' : ''}</Badge>}
          sectionKey="reporting" onRegenerate={() => fetchInsights('reporting')}
        />
        {expandedSections.reporting && (
          <CardContent className="pt-0 pb-4 px-4 space-y-5">
            {kpiMetrics.length > 0 && (
              <div>
                <SubHeading icon={<BarChart3 className="w-3 h-3 text-indigo-500" />}>Recommended KPIs</SubHeading>
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-3 py-2 font-semibold text-gray-600" style={{ fontWeight: 600 }}>Metric</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600 w-24" style={{ fontWeight: 600 }}>Type</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600 hidden sm:table-cell" style={{ fontWeight: 600 }}>Why It Matters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kpiMetrics.map((kpi, i) => {
                        const { type: effectiveType } = getEffectiveMetricType(kpi);
                        return (
                          <tr key={`km-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                            <td className="px-3 py-2 font-medium text-gray-900 align-top" style={{ fontWeight: 500 }}>
                              {kpi.name}
                              <p className="sm:hidden text-[11px] text-gray-500 mt-0.5 font-normal" style={{ fontWeight: 400 }}>{kpi.rationale}</p>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <MetricTypeBadgeReadOnly value={effectiveType} />
                            </td>
                            <td className="px-3 py-2 text-gray-600 hidden sm:table-cell align-top">{kpi.rationale}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {kpiTargets.length > 0 && (
              <div>
                <SubHeading icon={<Target className="w-3 h-3 text-emerald-500" />}>Target Outcomes</SubHeading>
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-3 py-2 font-semibold text-gray-600" style={{ fontWeight: 600 }}>Outcome</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600 w-24" style={{ fontWeight: 600 }}>Type</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600 hidden sm:table-cell" style={{ fontWeight: 600 }}>Rationale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kpiTargets.map((kpi, i) => {
                        const { type: effectiveType } = getEffectiveMetricType(kpi);
                        return (
                          <tr key={`kt-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                            <td className="px-3 py-2 font-medium text-gray-900 align-top" style={{ fontWeight: 500 }}>
                              {kpi.name}
                              <p className="sm:hidden text-[11px] text-gray-500 mt-0.5 font-normal" style={{ fontWeight: 400 }}>{kpi.rationale}</p>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <MetricTypeBadgeReadOnly value={effectiveType} />
                            </td>
                            <td className="px-3 py-2 text-gray-600 hidden sm:table-cell align-top">{kpi.rationale}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Required JSM Fields intentionally hidden from client view — available in exports and delivery workspace */}

            {reporting_blueprint.dashboards.length > 0 && (
              <div>
                <SubHeading icon={<Layout className="w-3 h-3 text-indigo-500" />}>Suggested Dashboards</SubHeading>
                <div className="space-y-1.5">
                  {reporting_blueprint.dashboards.map((d, i) => (
                    <div key={`db-${i}`} className="flex items-start gap-2 text-xs text-gray-700">
                      <Layout className="w-3.5 h-3.5 text-indigo-400 mt-0.5 shrink-0" />
                      <span><strong style={{ fontWeight: 600 }}>{d.name}:</strong> {d.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {nonDuplicateReportingInputs.length > 0 && (
              <div>
                <SubHeading icon={<AlertCircle className="w-3 h-3 text-amber-600" />}>Reporting Inputs to Confirm</SubHeading>
                <p className="text-[11px] text-gray-500 mb-2">
                  These items should be confirmed or configured before reporting can be fully operational.
                </p>
                <div className="space-y-2">
                  {nonDuplicateReportingInputs.map((item, i) => {
                    const key = insightKey(item.observation);
                    return (
                      <InsightCard
                        key={`mi-${i}`}
                        insight={item}
                        accentColor="amber"
                        status={insightStatuses[key] || 'open'}
                        onStatusChange={(s) => updateStatus(key, s)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        )}
        <ActionBar artifactType="reporting_blueprint" data={reporting_blueprint} regenKey="reporting" />
      </Card>
    </div>
  );
}